import { useEffect, useState } from 'react';
import { useLogger } from '@/lib/logger';
import { useBankCard } from './useBankCard';
import type { BankCardAuthForm } from '../types/bankCard';

const submitAuthForm = ({ actionEndpoint, formFields }: BankCardAuthForm) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionEndpoint;
  form.style.display = 'none';
  Object.entries(formFields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

/**
 * Opens the card partner's (Libra) form straight from the screen asking for
 * a card: no screen of our own in between, so Back from the partner returns
 * to that screen.
 */
export const useAddBankCardRedirect = () => {
  const logger = useLogger();
  const { initBankCardAuth } = useBankCard();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Back from the partner can restore this page as it was left: redirecting.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const startAddBankCard = async () => {
    if (isRedirecting) return;

    setIsRedirecting(true);
    setError(null);
    try {
      submitAuthForm(await initBankCardAuth());
    } catch (caught) {
      const failure = caught instanceof Error ? caught : new Error(String(caught));
      logger.error('Failed to initialize bank card auth', {
        error: failure.message,
      });
      setError(failure);
      setIsRedirecting(false);
    }
  };

  return { startAddBankCard, isRedirecting, error };
};
